import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PortfolioService } from '@shared/services/portfolio.service';
import { INewPortfolio, IPortfolio } from '@static/models/portfolio';
import { CdkTableModule } from "@angular/cdk/table";
import { MatSelectModule } from '@angular/material/select';
import { ProjectService } from '@shared/services/project.service';
import { IProject } from '@static/models/project';
import { ICompanyInformation } from '@static/models/company';
import { CompanyService } from '@core/site-admin/services/company.service';
import { ObservableService } from '@static/services/observable.service';
import { MatChipGrid, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-portfolio-add',
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    AsyncPipe,
    ReactiveFormsModule,
    CdkTableModule,
    MatChipGrid,
    MatChipsModule
  ],
  templateUrl: './portfolio-add.component.html',
  styleUrl: './portfolio-add.component.scss',
  providers: [
    ObservableService,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class PortfolioAddComponent {
  private _observableService: ObservableService = inject(ObservableService);
  private _companyService: CompanyService = inject(CompanyService);
  private _portfolioService: PortfolioService = inject(PortfolioService);
  private _projectService: ProjectService = inject(ProjectService);
  protected dialogRef: MatDialogRef<PortfolioAddComponent> = inject(MatDialogRef<PortfolioAddComponent>);
  protected data?: IPortfolio = inject(MAT_DIALOG_DATA);

  protected portfolioData: FormGroup;
  protected projectList: IProject[];
  protected filteredProjectList?: Observable<IProject[]>;
  protected projectMap: Map<string, IProject>;

  protected companyList: ICompanyInformation[];

  @ViewChild('projectInput') projectInput!: ElementRef<HTMLInputElement>;

  constructor() {
    this.portfolioData = new FormGroup({
      name: new FormControl(this.data?.name ?? '', [Validators.required]),
      description: new FormControl(this.data?.description ?? '', []),
      project: new FormControl(),
      selectedProjects: new FormControl(this.data?.projects ?? [], []),
      projectLogo: new FormControl(this.data?.company_logo ? +this.data.company_logo : undefined, []),
    });
    this.projectList = this._projectService.projectList.sort((a, b) =>
      (a.proj_full_name ?? a.proj_id) < (b.proj_full_name ?? b.proj_id) ? -1 : 1);
    this.projectMap = new Map(
      this.projectList.map(project => [project.proj_id, project])
    );

    this.filteredProjectList = this.portfolioData.get('project')!.valueChanges.pipe(
      startWith(this.projectInput?.nativeElement?.value ?? ''),
      map((value: string | IProject) => {
        const selectedIds = new Set(this.portfolioData.controls['selectedProjects'].value);

        if (value === undefined || value === null) {
          return this.projectList.filter(project => !selectedIds.has(project.proj_id));
        }

        if (typeof value === 'string') {
          const searchKey = value.toLowerCase();

          return this.projectList.filter((project: IProject) =>
            !selectedIds.has(project.proj_id) &&
            (
              `${project.proj_full_name}`.toLowerCase().includes(searchKey)
            )
          )
        } else {
          return this.projectList.filter((project: IProject) =>
            project.proj_id === value.proj_id && !selectedIds.has(project.proj_id)
          );
        }
      })
    );

    // Load the company list
    this.companyList = this._companyService.companyListUpdated.value;
    this._observableService.subscribe(
      this._companyService.companyListUpdated,
      (list) => this.companyList = list
    );
    if (!this.companyList.length) {
      this._companyService.getCompanyList();
    }
  }

  /**
   * Close the dialog
   */
  public close(): void {
    this.dialogRef.close(undefined);
  }

  /**
   * Add project to selection set
   * @param event the selected autocomplete option
   *
   */
  protected addProject(event: MatAutocompleteSelectedEvent) {
    const project = event.option.value;
    const control = this.portfolioData.controls['selectedProjects'];

    if (!control.value.some((p: IProject) => p.id === project.id)) {
      control.setValue([...control.value, project.proj_id]);
    }

    this.portfolioData.controls['project'].reset();
    this.projectInput.nativeElement.value = '';
  }

  /**
   * Remove project from selection set
   * @param project the project to remove
   *
   */
  removeProject(projectId: string) {
    const control = this.portfolioData.controls['selectedProjects'];

    control.setValue(
      control.value.filter((id: string) => id !== projectId)
    );

    // Refresh the list of projects when removing a project from the selection set
    this.portfolioData.controls['project'].setValue(
      this.portfolioData.controls['project'].value ?? ''
    )

    control.markAsDirty();
  }

  /**
   * Create or edit the portfolio with the entered data
   */
  public createPortfolio(): void {
    if (this.portfolioData.invalid) {
      this.portfolioData.markAllAsTouched();
      return;
    }

    const name = this.portfolioData.get('name')?.value;
    const desc = this.portfolioData.get('description')?.value;
    const projs = this.portfolioData.get('selectedProjects')?.value ?? undefined;
    const logo = this.portfolioData.get('projectLogo')?.value ?? undefined;
    if (name !== undefined) {
      if (this.data) {
        const data: IPortfolio = {
          id: this.data.id,
          name: name,
          description: desc,
          projects: projs,
          company_logo: logo?.toString(),
        }
        this._portfolioService.updatePortfolio(data);
        this.dialogRef.close();
      } else {
        const data: INewPortfolio = {
          name: name,
          description: desc,
          projects: projs,
          company_logo: logo?.toString(),
        }
        this._portfolioService.createPortfolio(data);
        this.dialogRef.close();
      }
    }
  }
}
